import { FC, useMemo, useState } from 'react';
import { OrderResponse, updateOrder } from '../../../api/orders';
import { Bay } from '../../../api/bays';
import { UserProfile } from '../../../api/auth';
import SchedulerItemBlock from './SchedulerItemBlock';
import AssignmentEditModal from './AssignmentEditModal';
import { getUtilizationColor, getUtilizationBgColor } from '../../../utils/utilization';

interface CarViewProps {
    orders: OrderResponse[];
    selectedDate: Date;
    bays: Bay[];
    technicians: UserProfile[];
    startHour: number;
    endHour: number;
    onRefresh: () => void;
}

const VehicleTimelineView: FC<CarViewProps> = ({ orders, selectedDate, bays, technicians, startHour, endHour, onRefresh }) => {
    const TOTAL_HOURS = endHour - startHour;

    const TIME_SLOTS = useMemo(() => {
        const slots = [];
        for (let i = startHour; i < endHour; i++) {
            const h = i % 12 || 12;
            const ampm = i < 12 || i === 24 ? 'AM' : 'PM';
            const displayH = i === 0 || i === 24 ? 12 : h;
            slots.push(`${displayH} ${ampm}`);
        }
        return slots;
    }, [startHour, endHour]);
    // Filter orders based on strict date overlap
    const filteredOrders = useMemo(() => {
        const rangeStart = new Date(selectedDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(selectedDate);
        rangeEnd.setHours(23, 59, 59, 999);

        return orders.filter(order => {
            // 1. Check if assigned on date (Overlap check)
            const hasAssignmentsOnDate = (order.serviceTechnicians || []).some(st => {
                if (!st.startTime || !st.endTime) return false;
                const start = new Date(st.startTime);
                const end = new Date(st.endTime);
                return start <= rangeEnd && end >= rangeStart;
            });

            // 2. Check if created on date
            const createdOnDate = new Date(order.createdAt).toDateString() === selectedDate.toDateString();

            // STRICT FILTER: Only show relevant orders
            // - Has assignment overlapping this day
            // - Created on this day
            return hasAssignmentsOnDate || createdOnDate;
        });
    }, [orders, selectedDate]);

    // Active assignments to display on the timeline
    const activeAssignments = useMemo(() => {
        const list: Array<{ order: OrderResponse; assignment: any }> = [];
        filteredOrders.forEach((order) => {
            (order.serviceTechnicians || []).forEach((st) => {
                if (st.startTime) {
                    const assignDate = new Date(st.startTime);
                    if (assignDate.toDateString() === selectedDate.toDateString()) {
                        list.push({ order, assignment: st });
                    }
                }
            });
        });
        return list;
    }, [filteredOrders, selectedDate]);

    const getTimePosition = (assignment: any) => {
        const startTime = assignment.startTime;
        const endTime = assignment.completed && assignment.completedAt ? assignment.completedAt : assignment.endTime;

        if (!startTime || !endTime) return { left: '0%', width: '0%' };

        const start = new Date(startTime);
        const end = new Date(endTime);

        let assignmentStart = start.getHours() + start.getMinutes() / 60;
        let assignmentEnd = end.getHours() + end.getMinutes() / 60;

        // Clamp to view range
        if (assignmentStart < startHour) assignmentStart = startHour;
        if (assignmentEnd > endHour) assignmentEnd = endHour;

        const left = ((assignmentStart - startHour) / TOTAL_HOURS) * 100;
        const width = ((assignmentEnd - assignmentStart) / TOTAL_HOURS) * 100;

        return { left: `${Math.max(0, left)}%`, width: `${Math.max(0, width)}%` };
    };

    const getBayUtilization = (bayId: string) => {
        const assignments = activeAssignments.filter(a => a.assignment.bayId === bayId);
        const totalDurationMs = assignments.reduce((acc, curr) => {
            if (curr.assignment.startTime && curr.assignment.endTime) {
                return acc + (new Date(curr.assignment.endTime).getTime() - new Date(curr.assignment.startTime).getTime());
            }
            return acc;
        }, 0);
        return Math.min(100, Math.round(((totalDurationMs / (1000 * 60 * 60)) / TOTAL_HOURS) * 100));
    };

    const getTechnicianUtilization = (techId: string) => {
        const assignments = activeAssignments.filter(a => a.assignment.technicianId === techId);
        const totalDurationMs = assignments.reduce((acc, curr) => {
            if (curr.assignment.startTime && curr.assignment.endTime) {
                return acc + (new Date(curr.assignment.endTime).getTime() - new Date(curr.assignment.startTime).getTime());
            }
            return acc;
        }, 0);
        return Math.min(100, Math.round(((totalDurationMs / (1000 * 60 * 60)) / TOTAL_HOURS) * 100));
    };

    const getOrderUtilization = (order: OrderResponse) => {
        const assignments = order.serviceTechnicians || [];
        if (assignments.length === 0) return 0;
        const total = assignments.length;
        const completed = assignments.filter(a => a.completed).length;
        return Math.round((completed / total) * 100);
    };

    const sidebarBays = useMemo(() => bays.slice(0, 10), [bays]);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState<{ order: OrderResponse; assignment: any } | null>(null);

    const handleAssignmentClick = (order: OrderResponse, assignment: any) => {
        setSelectedAssignment({ order, assignment });
        setEditModalOpen(true);
    };

    const handleSaveAssignment = async (assignmentId: string, data: any) => {
        const target = activeAssignments.find(a => a.assignment.id === assignmentId);
        if (!target) return;

        try {
            const currentOrderAssignments = (target.order.serviceTechnicians || []).map(st => {
                if (st.id === assignmentId) {
                    return {
                        orderItemId: st.orderItemId,
                        technicianId: st.technicianId,
                        bayId: data.bayId,
                        startTime: data.startTime,
                        endTime: data.endTime,
                        notes: data.notes,
                        completed: data.completed
                    };
                }
                return {
                    orderItemId: st.orderItemId,
                    technicianId: st.technicianId,
                    bayId: st.bayId,
                    startTime: st.startTime,
                    endTime: st.endTime,
                    notes: st.notes,
                    completed: st.completed
                };
            });

            await updateOrder(target.order.id, { serviceAssignments: currentOrderAssignments });
            onRefresh();
        } catch (error) {
            console.error('Failed to save assignment', error);
            alert('Failed to save assignment changes.');
        }
    };

    return (
        <div className="flex bg-[#f8faff] min-h-[600px]">
            {/* ... existing sidebar ... */}
            <div className="w-[240px] border-r border-slate-200 bg-white shrink-0">
                <div className="p-4 border-b border-slate-200 bg-slate-50/50">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Bays</h3>
                </div>
                <div className="p-4 space-y-4 overflow-y-auto max-h-[700px]">
                    {sidebarBays.map((bay) => {
                        const utilization = getBayUtilization(bay.id);
                        return (
                            <div key={bay.id} className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-700">{bay.name}</span>
                                    <span className={`text-[10px] font-bold ${getUtilizationColor(utilization)}`}>{utilization}%</span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${getUtilizationBgColor(utilization)}`} style={{ width: `${utilization}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Main Grid Area */}
            <div className="flex-1 overflow-x-auto">
                <div className="min-w-[1000px]">
                    {/* Header: Time Slots */}
                    <div className="flex border-b border-slate-200 bg-white sticky top-0 z-10">
                        <div className="w-[280px] shrink-0 p-4 border-r border-slate-200" /> {/* Spacer for Order column */}
                        <div className="flex flex-1">
                            {TIME_SLOTS.map((time) => (
                                <div key={time} className="flex-1 p-4 text-center border-l border-slate-100 font-bold text-[11px] text-slate-400 uppercase tracking-widest bg-slate-50/30">
                                    {time}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Body: Order Rows */}
                    <div className="divide-y divide-slate-200">
                        {filteredOrders.length === 0 ? (
                            <div className="p-12 text-center text-slate-400 italic">No orders with assignments for this date.</div>
                        ) : (
                            filteredOrders.map((order) => {
                                const orderAssignments = order.serviceTechnicians || [];

                                return (
                                    <div key={order.id} className="flex group">
                                        {/* Order Label (Detailed) */}
                                        <div className="w-[280px] shrink-0 p-4 border-r border-slate-200 bg-white group-hover:bg-slate-50 transition-colors">
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">SO: {order.orderNumber}</span>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[10px] text-emerald-500 font-bold">{getOrderUtilization(order)}%</span>
                                                        <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                                    </div>
                                                </div>
                                                <p className="text-xs font-bold text-slate-800">
                                                    {order.customerVehicle?.vehicleRegistrationNumber || 'N/A'}
                                                </p>

                                                <p className="text-[11px] text-slate-500">
                                                    {order.customerVehicle?.vehicleBrand?.name || ''} {order.customerVehicle?.vehicleModel?.name || ''}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Timeline Grid */}
                                        <div className="flex flex-1 relative min-h-[140px] bg-slate-50/20 group-hover:bg-transparent transition-colors">
                                            {/* Grid background markers */}
                                            {TIME_SLOTS.map((_, idx) => (
                                                <div key={idx} className="flex-1 border-l border-slate-100/50" />
                                            ))}

                                            {/* Service Blocks */}
                                            <div className="absolute inset-0 w-full h-full">
                                                {orderAssignments.length === 0 ? (
                                                    <div className="h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <span className="text-[10px] font-medium text-slate-300 uppercase tracking-wider">No assignments</span>
                                                    </div>
                                                ) : orderAssignments.map((assignment, idx) => {
                                                    const { left, width } = getTimePosition(assignment);
                                                    if (width === '0%') return null;
                                                    return (
                                                        <div
                                                            key={`${assignment.id}-${idx}`}
                                                            className="absolute top-2 bottom-2 p-1 cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
                                                            style={{ left, width }}
                                                            onClick={() => handleAssignmentClick(order, assignment)}
                                                        >
                                                            <div className="h-full w-full overflow-hidden">
                                                                <SchedulerItemBlock order={order} assignment={assignment} view="car" />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Right Sidebar: Technicians Status */}
            <div className="w-[180px] border-l border-slate-200 bg-white shrink-0 hidden lg:block">
                <div className="p-4 border-b border-slate-200 bg-slate-50/50">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Technicians</h3>
                </div>
                <div className="p-3 space-y-4">
                    {technicians.slice(0, 8).map((tech) => (
                        <div key={tech.id} className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-primary font-bold shadow-sm border border-slate-200 overflow-hidden">
                                {tech.firstName?.[0] || tech.username?.[0] || 'T'}
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-slate-800 truncate">{tech.firstName || tech.username}</p>
                                <span className={`text-[10px] font-bold ${getUtilizationColor(getTechnicianUtilization(tech.id))}`}>
                                    {getTechnicianUtilization(tech.id)}%
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {selectedAssignment && (
                <AssignmentEditModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    assignment={selectedAssignment.assignment}
                    order={selectedAssignment.order}
                    bays={bays}
                    onSave={handleSaveAssignment}
                />
            )}
        </div>
    );
};

export default VehicleTimelineView;
